import { useState, useEffect } from "react";

interface Theme {
  Rosewater: string;
  Flamingo: string;
  Pink: string;
  Mauve: string;
  Red: string;
  Maroon: string;
  Peach: string;
  Yellow: string;
  Green: string;
  Teal: string;
  Sky: string;
  Sapphire: string;
  Blue: string;
  Lavender: string;
  Text: string;
  Subtext1: string;
  Subtext0: string;
  Overlay2: string;
  Overlay1: string;
  Overlay0: string;
  Surface2: string;
  Surface1: string;
  Surface0: string;
  Base: string;
  Mantle: string;
  Crust: string;
}

const themes = {
  Light: {
    Rosewater: "#c84053",
    Flamingo: "#c84053",
    Pink: "#b35b79",
    Mauve: "#624c83",
    Red: "#c84053",
    Maroon: "#c84053",
    Peach: "#cc6d00",
    Yellow: "#77713f",
    Green: "#6f894e",
    Teal: "#597b75",
    Sky: "#4d699b",
    Sapphire: "#4d699b",
    Blue: "#4d699b",
    Lavender: "#624c83",
    Text: "#545464",
    Subtext1: "#716e61",
    Subtext0: "#716e61",
    Overlay2: "#8a8980",
    Overlay1: "#8a8980",
    Overlay0: "#9e9b93",
    Surface2: "#b5b09a",
    Surface1: "#ded7b4",
    Surface0: "#ece6c8",
    Base: "#f7f4e4",
    Mantle: "#efeacd",
    Crust: "#f2eeda",
  },
  Dark: {
    Rosewater: "#c4746e",
    Flamingo: "#c4746e",
    Pink: "#a292a3",
    Mauve: "#8992a7",
    Red: "#c4746e",
    Maroon: "#c4746e",
    Peach: "#b6927b",
    Yellow: "#c4b28a",
    Green: "#8a9a7b",
    Teal: "#8ea4a2",
    Sky: "#8ba4b0",
    Sapphire: "#8ba4b0",
    Blue: "#8ba4b0",
    Lavender: "#8992a7",
    Text: "#c5c9c5",
    Subtext1: "#a6a69c",
    Subtext0: "#a6a69c",
    Overlay2: "#737c73",
    Overlay1: "#737c73",
    Overlay0: "#625e5a",
    Surface2: "#625e5a",
    Surface1: "#393836",
    Surface0: "#282727",
    Base: "#181616",
    Mantle: "#1d1c1a",
    Crust: "#0d0c0c",
  },
};

export default function ThemeButton() {
  const [currentTheme, setCurrentTheme] = useState("Light");

  useEffect(() => {
    const savedTheme = localStorage.getItem("portfolio-theme");
    if (savedTheme === "Light" || savedTheme === "Dark") {
      setCurrentTheme(savedTheme);
      changeTheme(savedTheme);
    }
  }, []);

  const toggleTheme = () => {
    const wantedTheme = currentTheme === "Light" ? "Dark" : "Light";
    setCurrentTheme(wantedTheme);
    changeTheme(wantedTheme);
    localStorage.setItem("portfolio-theme", wantedTheme);
  };

  return (
    <button
      onClick={toggleTheme}
      className="
        group
        relative
        flex
        items-center
        justify-center
        w-9
        h-9
        bg-transparent
        text-Subtext0
        cursor-pointer
        transition-colors
        duration-200
        hover:text-Sapphire
      "
      aria-label="Toggle Theme"
    >
      <div className="relative w-5 h-5">
        {/* Sun Icon (Light Mode) — Lucide "sun" */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`
            absolute top-0 left-0 w-full h-full
            transition-all duration-300 ease-in-out
            ${
              currentTheme === "Light"
                ? "rotate-0 opacity-100 scale-100"
                : "-rotate-90 opacity-0 scale-0"
            }
          `}
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>

        {/* Moon Icon (Dark Mode) — Lucide "moon" */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`
            absolute top-0 left-0 w-full h-full
            transition-all duration-300 ease-in-out
            ${
              currentTheme === "Dark"
                ? "rotate-0 opacity-100 scale-100"
                : "rotate-90 opacity-0 scale-0"
            }
          `}
        >
          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
        </svg>
      </div>
    </button>
  );
}

function changeTheme(wantedTheme: string) {
  try {
    const requestedTheme = getTheme(wantedTheme);
    Object.entries(requestedTheme).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--color-${key}`, value);
    });
  } catch (error) {
    console.error("Failed to change theme:", error);
  }
}

function getTheme(wantedTheme: string): Theme {
  if (wantedTheme === "Light") {
    return themes.Light;
  } else if (wantedTheme === "Dark") {
    return themes.Dark;
  }
  throw new Error("Invalid theme flavor");
}

